import {SelectItem} from "@mantine/core";
import {Entity} from "@ots-frontend/ots-data";
import {useCallback, useEffect, useMemo, useState} from "react";
import {BaseSelect, BaseSelectProps} from "../base-select";
import {useTranslation} from "react-i18next";

export type BaseEntitySelectProps<E extends Entity> = Omit<BaseSelectProps, 'data'> & {
  entities?: E[];
  valueMapper?: (e: E) => string;
  nameMapper?: (e: E) => string;
  itemDataMapper?: (e: E) => Record<string, unknown>;
  onEntityChange?: (entities: E[]) => void;
  searchResultsOnly?: boolean;
  appendDnu?: boolean
  defaultToFirst?: boolean;
};

export function BaseEntitySelect<E extends Entity>(
  props: BaseEntitySelectProps<E>
) {
  const {t} = useTranslation(['common'])

  const {
    entities = [],
    valueMapper,
    nameMapper,
    itemDataMapper,
    onChange,
    onEntityChange,
    onSearchChange,
    searchValue,
    searchable = true,
    searchResultsOnly = false,
    appendDnu = true,
    defaultToFirst = false,
    nothingFound = t('common.state.noresults', 'No Results'),
    value: controlledValue,
    ...rest
  } = props;

  const [setToFirst, setSetToFirst] = useState(false);
  const [internalValue, setInternalValue] = useState<string | null>(null)
  const [internalSearchValue, setInternalSearchValue] = useState('');

  const [valueMap, setValueMap] = useState<Record<string, E>>({});

  const memoValueMapper = useMemo(() => {
    return valueMapper ?? ((e: E) => e.id)
  }, [valueMapper]);

  const memoNameMapper = useMemo(() => {
    return nameMapper ?? ((e: E) => e.displayName)
  }, [nameMapper]);

  const _onSearchChange = useCallback((query: string) => {
    setInternalSearchValue(query)
  }, [])

  const _onChange = useCallback(
    (value: string | null) => {
      if (onEntityChange) {
        let selectedEntities: E[] = [];

        if (value) {
          selectedEntities = Object.keys(valueMap)
            .filter((k) => k === value)
            .map((k) => valueMap[k]);
        }

        onEntityChange(selectedEntities);
      }

      if (onChange) {
        onChange(value);
      }
    },
    [valueMap, onChange, onEntityChange]
  );

  const data = useMemo(() => {
    const items: SelectItem[] = []

    for (const e of entities) {
      let label = memoNameMapper(e)
      if (appendDnu && e.doNotUse) {
        label += " (DNU)"
      }

      const item = {
        ...(itemDataMapper?.(e) ?? {}),
        value: memoValueMapper(e),
        label: label,
      }

      if (searchResultsOnly) {
        if (internalSearchValue.length > 0) {
          if (item.value.toLowerCase().includes(internalSearchValue.toLowerCase())) {
            items.push(item)
          } else if (item.label.toLowerCase().includes(internalSearchValue.toLowerCase())) {
            items.push(item)
          }
        }
      } else {
        items.push(item)
      }
    }

    return items
  }, [entities, memoValueMapper, memoNameMapper, itemDataMapper, searchResultsOnly, internalSearchValue, appendDnu])

  useEffect(() => {
    const itemMap: Record<string, E> = {}

    for (const e of entities) {
      const value = memoValueMapper(e)
      itemMap[value] = e
    }

    setValueMap(itemMap)
  }, [entities, memoValueMapper]);

  useEffect(() => {
    if (defaultToFirst && !setToFirst && !controlledValue && data?.[0]?.value) {
      setSetToFirst(true);
      setInternalValue(data[0].value);
      _onChange(data[0].value);
    }
  }, [defaultToFirst, data, controlledValue, _onChange, setToFirst]);

  return (
    <BaseSelect
      data={data}
      value={controlledValue ?? internalValue}
      onChange={_onChange}
      searchable={searchable}
      onSearchChange={searchResultsOnly ? _onSearchChange : onSearchChange}
      searchValue={searchResultsOnly ? internalSearchValue : searchValue}
      nothingFound={nothingFound}
      {...rest} />
  );
}
